import * as React from 'react';
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import type { SalesUser } from '@ooh/contracts';

/**
 * Shared by the create-offer form and the quick-add-offer-picker's inline
 * "creating" mode — both need identical role-dependent default/required
 * salesperson behavior. See
 * openspec/changes/offer-salesperson-assignment/tasks/research-web.md.
 *
 * Takes `authUserId` (not the full `UserProfile`) so components consumed
 * directly by `apps/mockups` (which have no `@tanstack/react-router`
 * context) can source it from a plain prop instead of calling
 * `useRouteContext` themselves.
 */

/**
 * "Sales department" membership is derived from whether the caller's own id
 * appears in the sales-users list response, not from any separate role/
 * department field.
 */
/**
 * `canAssignOtherSalesperson` defaults to `true` (fail-open, matches
 * `useHasPermission`'s loading behavior) so callers that haven't resolved the
 * `offer.assign_other_salesperson` permission yet don't spuriously lock the
 * field. Only locks the field (`fieldDisabled`) for sales-department callers
 * who explicitly lack the permission — non-sales-department callers are
 * unaffected, since they never get a self-default in the first place and
 * must always pick a salesperson explicitly.
 */
export function resolveSalespersonGating(
  salesUsers: SalesUser[],
  authUserId: number | null | undefined,
  salespersonId: number | undefined,
  isSubmitPending = false,
  canAssignOtherSalesperson = true,
): { isSalesDepartment: boolean; submitDisabled: boolean; fieldDisabled: boolean } {
  const isSalesDepartment = salesUsers.some((user) => user.id === authUserId);
  const submitDisabled = isSubmitPending || (!isSalesDepartment && salespersonId == null);
  const fieldDisabled = isSalesDepartment && !canAssignOtherSalesperson;
  return { isSalesDepartment, submitDisabled, fieldDisabled };
}

/**
 * Shared `{ label, value }` option mapping for the salesperson picker, used by both
 * the create-offer form and the quick-add-offer-picker's inline "creating" mode so
 * the two pickers can't silently diverge in shape.
 */
export function toSalesUserOptions(salesUsers: SalesUser[]): { label: string; value: string }[] {
  return salesUsers.map((user) => ({ label: user.name, value: String(user.id) }));
}

interface FormValuesWithSalespersonId extends FieldValues {
  salespersonId?: number;
}

/**
 * Prefills `salespersonId` to the caller's own id, once, for sales-department
 * callers whose field is still empty — never re-applies once a value has
 * been set (by this effect or by the user), so a later override sticks.
 *
 * Deferred to a microtask: Radix's `Select` fires a spurious native "change"
 * event on its hidden bridge `<select>` shortly after mount (an internal
 * implementation detail unrelated to any user action), which would otherwise
 * race with and immediately undo a same-tick `setValue` call. Deferring past
 * that lets the prefill land after Radix's own mount-time bookkeeping
 * settles.
 */
export function useSalespersonSelfDefault<TFieldValues extends FormValuesWithSalespersonId>(
  offerForm: UseFormReturn<TFieldValues>,
  isSalesDepartment: boolean,
  authUserId: number | null | undefined,
) {
  const selfUserId = authUserId;
  const salespersonIdPath = 'salespersonId' as Path<TFieldValues>;

  React.useEffect(() => {
    if (!isSalesDepartment) return;
    if (offerForm.getValues(salespersonIdPath) != null) return;
    if (selfUserId == null) return;

    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      if (offerForm.getValues(salespersonIdPath) != null) return;
      offerForm.setValue(
        salespersonIdPath,
        selfUserId as PathValue<TFieldValues, Path<TFieldValues>>,
        { shouldDirty: false },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [isSalesDepartment, offerForm, salespersonIdPath, selfUserId]);
}
